// Game abstraction layer
// All game-specific configuration lives here.
// To add a new game, create a new GameConfig object.
// --- Chess Configuration ---
export const CHESS_CONFIG = {
    id: 'chess',
    name: 'Chess',
    hasColors: true,
    colorNames: { first: 'White', second: 'Black' },
    colorCodes: { first: 'w', second: 'b' },
    hasDraws: true,
    resultOptions: [
        { value: '1-0', label: '1-0', firstPlayerScore: 1 },
        { value: '0-1', label: '0-1', firstPlayerScore: 0 },
        { value: '1/2-1/2', label: '½-½', firstPlayerScore: 0.5 },
    ],
    byeResult: '+/-',
    forfeitWin: '+/-',
    forfeitLoss: '-/+',
    resultLabels: { win: 'W', loss: 'L', draw: 'D', bye: 'BYE' },
    playerFields: {
        rating: true,
        ratingLabel: 'Rating',
        externalId: true,
        externalIdLabel: 'FIDE ID',
    },
    timeControls: ['Classical', 'Rapid', 'Blitz'],
    tournamentSystems: ['swiss', 'round_robin', 'double_round_robin'],
    tiebreakCriteria: [
        'Buchholz',
        'Buchholz Cut-1',
        'Sonneborn-Berger',
        'Direct Encounter',
        'Number of Wins',
        'Average Rating of Opponents',
        'Koya System',
        'Number of Games with Black',
    ],
    integerTiebreaks: ['Number of Wins', 'Number of Games with Black'],
    koOnlyTiebreaks: [],
};
// --- Backgammon Configuration ---
export const BACKGAMMON_CONFIG = {
    id: 'backgammon',
    name: 'Backgammon',
    hasColors: false,
    colorNames: { first: 'Player 1', second: 'Player 2' },
    colorCodes: { first: '1', second: '2' },
    hasDraws: false,
    resultOptions: [
        { value: '1-0', label: '1-0', firstPlayerScore: 1 },
        { value: '0-1', label: '0-1', firstPlayerScore: 0 },
    ],
    byeResult: '+/-',
    forfeitWin: '+/-',
    forfeitLoss: '-/+',
    resultLabels: { win: 'W', loss: 'L', bye: 'BYE' },
    playerFields: {
        rating: false,
        ratingLabel: 'Rating',
        externalId: false,
        externalIdLabel: 'Player ID',
    },
    timeControls: ['Standard', 'Speed', 'No clocks'],
    tournamentSystems: ['swiss', '3ko', '5ko', 'round_robin', 'double_round_robin'],
    tiebreakCriteria: [
        'Tournament Progress',
        'Buchholz',
        'Buchholz Cut-1',
        'Sonneborn-Berger',
        'Direct Encounter',
        'Number of Wins',
    ],
    integerTiebreaks: ['Number of Wins'],
    koOnlyTiebreaks: ['Tournament Progress'],
};
// --- Generic / Other Game Configuration ---
export const OTHER_CONFIG = {
    id: 'other',
    name: 'Other',
    hasColors: true,
    colorNames: { first: 'Player 1', second: 'Player 2' },
    colorCodes: { first: '1', second: '2' },
    hasDraws: true,
    resultOptions: [
        { value: '1-0', label: '1-0', firstPlayerScore: 1 },
        { value: '0-1', label: '0-1', firstPlayerScore: 0 },
        { value: '1/2-1/2', label: '½-½', firstPlayerScore: 0.5 },
    ],
    byeResult: '+/-',
    forfeitWin: '+/-',
    forfeitLoss: '-/+',
    resultLabels: { win: 'W', loss: 'L', draw: 'D', bye: 'BYE' },
    playerFields: {
        rating: false,
        ratingLabel: 'Rating',
        externalId: false,
        externalIdLabel: 'Player ID',
    },
    timeControls: [],
    tournamentSystems: ['swiss', 'round_robin', 'double_round_robin'],
    tiebreakCriteria: [
        'Buchholz',
        'Buchholz Cut-1',
        'Sonneborn-Berger',
        'Direct Encounter',
        'Number of Wins',
    ],
    integerTiebreaks: ['Number of Wins'],
    koOnlyTiebreaks: [],
};
// --- BMAB grading structure (story 045) ---
//
// Single source of truth shared by the SQL CHECK, the form dropdown, and the
// type union. Codes are the canonical BMAB identifiers (sortable, stable);
// labels are for display only.
export const BMAB_TITLES = [
    { code: 'S1', label: 'Super Grandmaster 1', group: 'mastership' },
    { code: 'S2', label: 'Super Grandmaster 2', group: 'mastership' },
    { code: 'S3', label: 'Super Grandmaster 3', group: 'mastership' },
    { code: 'G0', label: 'Grandmaster 0', group: 'mastership' },
    { code: 'G1', label: 'Grandmaster 1', group: 'mastership' },
    { code: 'G2', label: 'Grandmaster 2', group: 'mastership' },
    { code: 'G3', label: 'Grandmaster 3', group: 'mastership' },
    { code: 'M1', label: 'Master 1', group: 'mastership' },
    { code: 'M2', label: 'Master 2', group: 'mastership' },
    { code: 'M3', label: 'Master 3', group: 'mastership' },
    { code: 'A1', label: 'Advanced 1', group: 'proficiency' },
    { code: 'A2', label: 'Advanced 2', group: 'proficiency' },
    { code: 'A3', label: 'Advanced 3', group: 'proficiency' },
    { code: 'I1', label: 'Intermediate 1', group: 'proficiency' },
    { code: 'I2', label: 'Intermediate 2', group: 'proficiency' },
    { code: 'I3', label: 'Intermediate 3', group: 'proficiency' },
];
export function isBmabTitleCode(value) {
    return typeof value === 'string' && BMAB_TITLES.some((t) => t.code === value);
}
// FIDE chess titles used in the TRF16 rating report (story 096). The 8 OTB
// titles: GM/IM/FM/CM and the women's WGM/WIM/WFM/WCM. Order is highest-first
// for picker display.
export const FIDE_TITLES = [
    { code: 'GM', label: 'Grandmaster' },
    { code: 'IM', label: 'International Master' },
    { code: 'FM', label: 'FIDE Master' },
    { code: 'CM', label: 'Candidate Master' },
    { code: 'WGM', label: 'Woman Grandmaster' },
    { code: 'WIM', label: 'Woman International Master' },
    { code: 'WFM', label: 'Woman FIDE Master' },
    { code: 'WCM', label: 'Woman Candidate Master' },
];
export function isFideTitleCode(value) {
    return typeof value === 'string' && FIDE_TITLES.some((t) => t.code === value);
}
// Player sex as modeled by the FIDE TRF (only these two values).
export const PLAYER_SEXES = ['m', 'f'];
export function isPlayerSex(value) {
    return value === 'm' || value === 'f';
}
// Game registry
const GAMES = {
    chess: CHESS_CONFIG,
    backgammon: BACKGAMMON_CONFIG,
    other: OTHER_CONFIG,
};
export function getGameConfig(gameId = 'chess') {
    return GAMES[gameId] ?? OTHER_CONFIG;
}
/** Get the score for a given result from the perspective of the first player */
export function getResultScore(config, result, isFirstPlayer) {
    // Check standard results
    const option = config.resultOptions.find((r) => r.value === result);
    if (option) {
        return isFirstPlayer ? option.firstPlayerScore : (1 - option.firstPlayerScore);
    }
    // Bye/forfeit (including TRF variants)
    if (result === config.byeResult || result === config.forfeitWin || result === '+--') {
        return isFirstPlayer ? 1 : 0;
    }
    if (result === config.forfeitLoss || result === '--+') {
        return isFirstPlayer ? 0 : 1;
    }
    // Draw variants (TRF: 1/2Z, Z1/2)
    if (result.includes('1/2'))
        return 0.5;
    return 0;
}
/** Map a raw result to a display label (W/D/L) */
export function getResultLabel(config, score) {
    if (score === 1)
        return config.resultLabels.win;
    if (score === 0)
        return config.resultLabels.loss;
    return config.resultLabels.draw ?? config.resultLabels.win;
}
//# sourceMappingURL=games.js.map